<script setup lang="ts">
import { onClickOutside, useFocus, useMagicKeys } from '@vueuse/core'
import { getProductPermalink, getSearchPermalink } from '~/lib'
import { useI18n } from 'vue-i18n'
const localePath = useLocalePath()

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    displayTotal?: number
  }>(),
  { displayTotal: 5, order: 'is-set' },
)

const { searchTerm, search, getProducts, getTotal, loading } = useProductSearchSuggest()

// True when the search bar is active and user can type in the search field
const active = ref(false)

// Reference to the search container
const searchContainer = ref(null)
const searchInput = ref()

watch(active, (value) => {
  const { focused } = useFocus(searchInput)

  focused.value = value
})

// String the user has typed in the search field
const currentRoute = useRoute()
const router = useRouter()
const typingQuery = ref((currentRoute.query.search as string) || '')

// Defer the search request to prevent the search from being triggered too after typing
const performSuggestSearch = useDebounceFn((value) => {
  searchTerm.value = value
  search({ order: 'is-set' })
  // Update URL to reflect current search
  if (currentRoute.path.includes('/search')) {
    router.replace({
      query: { ...currentRoute.query, search: value },
    })
  }
}, 300)

watch(typingQuery, (value) => {
  if (value.length >= 3) {
    performSuggestSearch(value)
  }
})

// Suggest results will only be shown, when the search bar is active and the user has typed more than three characters in the search field
const showSuggest = computed(() => {
  return typingQuery.value.length >= 3 && active.value
})

// Only set up click outside on client side to avoid SSR issues
onMounted(() => {
  onClickOutside(searchContainer, () => {
    active.value = false
  })
})

const keys = useMagicKeys({ target: searchInput })
const enter = computed(() => keys.enter?.value ?? false)
const { push } = router

watch(enter, (value) => {
  if (!value) return
  active.value = false
  push(`${localePath(getSearchPermalink())}?search=${typingQuery.value}`)
})

const results = computed(() => {
  return (
    getProducts.value?.slice(0, props.displayTotal).map((product) => ({
      id: product.id,
      link: {
        text: product.translated.name || '',
        url: localePath(getProductPermalink(product)),
      },
    })) || []
  )
})

const handleSearch = () => {
  if (!typingQuery.value) return
  active.value = false
  push(`${localePath(getSearchPermalink())}?search=${typingQuery.value}`)
}
</script>

<template>
  <div ref="searchContainer" v-animate="{ animation: 'vertical-reveal' }" class="wrapper">
    <div class="results-wrapper">
      <SearchField
        ref="searchInput"
        v-model="typingQuery"
        :placeholder="t('shop.listingPage.search.label')"
        @search="handleSearch"
        @click="active = true"
      />
      <div v-if="showSuggest" class="results">
        <LinkList :items="results" />
        <div v-if="loading" class="text-small">{{ t('shop.listingPage.search.loading') }}</div>
        <div v-else :style="{ textAlign: 'right' }">
          <Button
            v-if="getTotal > 1"
            secondary
            slide
            :to="`${localePath(getSearchPermalink())}?search=${typingQuery}&order=is-set`"
            :aria-label="t('shop.listingPage.search.allResults')"
            @click=";[(active = false)]"
          >
            {{ t('shop.listingPage.search.allResults') }}
          </Button>
          <div v-else>
            <span class="text-small">{{ t('shop.listingPage.search.noResults') }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
.wrapper {
  .results-wrapper {
    position: relative;
    width: 100%;
    .results {
      position: absolute;
      box-shadow: var(--shadow);
      background-color: var(--c-white);
      padding: 20px;
      width: 100%;

      z-index: 99;
    }

    .link-list {
      margin-bottom: 24px;
    }
    .button {
      width: 100%;
      @include breakpoint(ts) {
        max-width: 400px;
      }
    }
  }
}
</style>
